import { Box, MenuItem, Typography } from '@mui/material';
import { Fragment, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS } from '@callmart/types';
import CustomSelect, { CustomSelectProps } from '../UI/CustomSelect';
import { WorkingUnit } from '../../types/WorkingUnit.interface';
import { compareWorkingUnits } from '../../utils';
import dayjs from '@utils/dayjs';

export type WorkingUnitOption = {
    workingUnitLocationId?: number;
    latestDataMonth?: string;
    correctedTotalsReleasedThroughMonth?: string;
    correctedTotalsDraftThroughMonth?: string;
} & Omit<WorkingUnit, 'matchWithOfferedCluster'>;

export type WorkingUnitSelectProps = {
    selectedWorkingUnit: number;
    workingUnitOptions: WorkingUnitOption[];
    valueProp: 'id' | 'worktypeId' | 'channelId' | 'taskId' | 'workingUnitLocationId';
    onChange: (selectedWorkingUnit: number) => void;
    sortByName?: boolean;
    label?: string;
    loading?: boolean;
    error?: boolean;
} & Omit<CustomSelectProps<number>, 'onChange' | 'selectedValue' | 'options' | 'label'>;

const WorkingUnitSelect = ({
    selectedWorkingUnit,
    onChange,
    workingUnitOptions,
    sortByName = true,
    label = 'Working Unit',
    valueProp = 'id',
    loading = false,
    error = false,
    ...props
}: WorkingUnitSelectProps) => {
    const { t } = useTranslation();
    const sortedOptions = useMemo(() => {
        return sortByName ? [...workingUnitOptions].sort(compareWorkingUnits) : workingUnitOptions;
    }, [workingUnitOptions, sortByName]);

    const getWorkingUnitLabel = (workingUnit: WorkingUnitOption) =>
        [workingUnit?.worktype?.worktype, workingUnit?.channel?.name, workingUnit?.task?.name]
            .filter(Boolean)
            .join(' - ');

    // Compact per-option metadata, rendered on a single caption line separated by " | ".
    const getMetaSegments = (workingUnit: WorkingUnitOption): { key: string; node: ReactNode }[] => {
        const formatMonth = (month: string) => dayjs(month).format(DATE_FORMATS.MONTH_NAME_YEAR);
        const segments: { key: string; node: ReactNode }[] = [];

        if (workingUnit.latestDataMonth && dayjs(workingUnit.latestDataMonth).isValid()) {
            segments.push({
                key: 'latest',
                node: `${t('field.latestData')}: ${formatMonth(workingUnit.latestDataMonth)}`,
            });
        }

        const releasedMonth =
            workingUnit.correctedTotalsReleasedThroughMonth &&
            dayjs(workingUnit.correctedTotalsReleasedThroughMonth).isValid()
                ? workingUnit.correctedTotalsReleasedThroughMonth
                : undefined;
        const draftMonth =
            workingUnit.correctedTotalsDraftThroughMonth &&
            dayjs(workingUnit.correctedTotalsDraftThroughMonth).isValid()
                ? workingUnit.correctedTotalsDraftThroughMonth
                : undefined;

        // Show only the freshest corrected-totals state: a draft wins when it is at least as new as
        // the latest released month (pending work), otherwise show the released month. 'YYYY-MM'
        // strings compare chronologically.
        if (draftMonth && (!releasedMonth || draftMonth >= releasedMonth)) {
            segments.push({
                key: 'corrected',
                node: (
                    <Box component="span" sx={{ color: 'warning.main' }}>
                        {t('bidding.drafts')}: {formatMonth(draftMonth)}
                    </Box>
                ),
            });
        } else if (releasedMonth) {
            segments.push({ key: 'corrected', node: `${t('status.corrected')}: ${formatMonth(releasedMonth)}` });
        }

        return segments;
    };

    const renderSelectedValue = (value: number) => {
        // notranslate on every branch: if a translation extension rewrites the loading
        // placeholder, React's swap to the real label leaves the extension's stale
        // "Load..." text stuck as the displayed value.
        if (loading) {
            return <span className="notranslate">{t('common.loading')}</span>;
        }
        if (error) {
            return <span className="notranslate">{t('error.loading')}</span>;
        }
        const selected = sortedOptions.find((wu) => wu[valueProp] === value);
        if (!selected) return null;
        return <span className="notranslate">{getWorkingUnitLabel(selected)}</span>;
    };

    return (
        <CustomSelect
            selectedValue={selectedWorkingUnit}
            options={sortedOptions}
            onChange={onChange}
            label={label}
            disabled={loading}
            renderValue={renderSelectedValue}
            {...props}
        >
            {sortedOptions &&
                sortedOptions.map((workingUnit) => {
                    const metaSegments = getMetaSegments(workingUnit);
                    return (
                        <MenuItem value={workingUnit[valueProp]} key={workingUnit[valueProp]}>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <span className="notranslate">{getWorkingUnitLabel(workingUnit)}</span>
                                {metaSegments.length > 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                        {metaSegments.map((segment, index) => (
                                            <Fragment key={segment.key}>
                                                {index > 0 && <span> | </span>}
                                                {segment.node}
                                            </Fragment>
                                        ))}
                                    </Typography>
                                )}
                            </Box>
                        </MenuItem>
                    );
                })}
        </CustomSelect>
    );
};

export default WorkingUnitSelect;
